/***
 * Guard to prevent access to tabs in the group component. If trying to access a
 * tab that one does not have permission for, will default to an initial tab.
 *
 * TODO: Magic strings for the routes could be refactored
 * TODO: Logic for the permissions
 * TODO: Better handling of the groupInfo so that we don't have to get it for every tab (need to update it when we make changes to the group data)
 */

import { Injectable, NgZone } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { ContextService } from '@app/shared/services/context.service';
import { GroupService } from '@app/shared/services/group.service';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { GroupInfo } from '../group-api';

@Injectable({
  providedIn: 'root',
})
export class GroupTabAccessGuard  {
  public groupPath = '/groups';

  constructor(
    private groupService: GroupService,
    private router: Router,
    private contextService: ContextService
  ) {}

  public canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    // We're going to need to always get this unless we're sure we're updating the cache
    // when we make changes to the group object.
    const groupId = childRoute.parent.params.id;
    return this.groupService.getSummary(groupId).pipe(
      map((data) => {
        this.groupService.groupInfo = data;

        if (!this.canAccess(childRoute.routeConfig.path, data)) {
          const initialPage = this.getInitialPage(data);
          // avoid circular loop where you can't access the initial page
          if (childRoute.routeConfig.path === initialPage) {
            this.router.navigateByUrl(`/error-handler/404`);
            return false;
          }

          this.router.navigate([this.groupPath, groupId, initialPage])
          return false;
        }

        return true;
      }),
      catchError((error) => {
        this.router.navigateByUrl(`/error-handler/404`);
        return of(false);
      })
    );
  }

  private canAccess(path: string, group: GroupInfo) {
    switch (path) {
      case 'feed':
        return this.canViewFeed(group);
      case 'members':
        return this.canViewMembers(group);
      case 'groupInsights':
        return this.canViewReporting(group);
      case 'organizations':
        return this.canViewOrganizations(group);
      case 'settings':
        return this.canViewSettings(group);
      default:
        return true;
    }
  }

  private getInitialPage(group: GroupInfo) {
    if (this.canViewFeed(group)) {
      return 'feed';
    } else if (this.canViewMembers(group)) {
      return 'members';
    } else if (this.canViewReporting(group)) {
      return 'groupInsights';
    }
  }

  private canViewFeed(group: GroupInfo) {
    return !group.isAdministrativeGroup;
  }

  private canViewMembers(group: GroupInfo) {
    return group.userGroupPermissions?.includes('ViewMembers');
  }

  private canViewReporting(group: GroupInfo) {
    return (
      group.userGroupPermissions?.includes('ViewGroupReports') ||
      group.canManageGroups
    );
  }

  private canViewSettings(group: GroupInfo) {
    return (
      group.userGroupPermissions?.includes('EditPermissions') ||
      group.userGroupPermissions?.includes('EditSettings') ||
      group.userGroupPermissions?.includes('DeleteGroup')
    );
  }

  private canViewOrganizations(group: GroupInfo) {
    const isChannelContext = this.contextService.urlHasContext(
      window.location.href,
      'channel'
    );
    return isChannelContext && group.canManageGroups;
  }
}
